import type { QueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { pcmpAccountsQueryKeys } from '../api/pcmpAccounts.api';
import { pcmpCorrectionQueryKeys } from '../api/pcmpCorrection.api';
import { pcmpDisburseVerifyQueryKeys } from '../api/pcmpDisburseVerify.api';
import { pcmpMarketingQueryKeys } from '../api/pcmpMarketing.api';
import { pcmpMisQueryKeys } from '../api/pcmpMis.api';
import { pcmpProposalQueryKeys } from '../api/pcmpProposals.api';
import { pcmpRequestQueryKeys } from '../api/pcmpRequests.api';

// 403/409 mean the proposal moved on since it was shown (someone else acted,
// it was held, MIS routed it): refresh so the buttons follow.
const STALE_STATUSES = new Set([403, 409]);

/**
 * Success and error handling shared by the Marketing, MIS, Accounts and correction
 * proposal and batch actions. Errors stay in the dialog (shown inline), never a toast.
 */
export function pcmpActionCallbacks<R = unknown>(
  queryClient: QueryClient,
  successTitle: string | ((result: R) => string),
  onSuccess: (result: R) => void,
) {
  const refresh = () => {
    // Every detail: a School with Teacher reject also closes sibling proposals.
    void queryClient.invalidateQueries({ queryKey: pcmpProposalQueryKeys.details() });
    // One lane's action moves rows on the others (e.g. MIS Not Verified, a
    // published Accounts batch, an Accounts failure on the correction count).
    void queryClient.invalidateQueries({ queryKey: pcmpMarketingQueryKeys.all() });
    void queryClient.invalidateQueries({ queryKey: pcmpMisQueryKeys.all() });
    void queryClient.invalidateQueries({ queryKey: pcmpAccountsQueryKeys.all() });
    void queryClient.invalidateQueries({ queryKey: pcmpCorrectionQueryKeys.all() });
    // A payout, a failure or a return moves rows on and off the Disburse Verify tabs.
    void queryClient.invalidateQueries({ queryKey: pcmpDisburseVerifyQueryKeys.all() });
    // Hold, reject, return and payment-source changes move the request lists and badges.
    void queryClient.invalidateQueries({ queryKey: pcmpRequestQueryKeys.all() });
  };

  return {
    onSuccess: (result: R) => {
      refresh();
      toast.success(typeof successTitle === 'function' ? successTitle(result) : successTitle);
      onSuccess(result);
    },
    onError: (error: unknown) => {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (status !== undefined && STALE_STATUSES.has(status)) refresh();
    },
  };
}
